/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useApiData } from './ApiData';
import { OrgGoal, Stats } from '@/types';
import { scoreResult2ScoresObject } from '@/lib/mapping';
import getQuestionsForSurveyType from '../lib/getQuestionsForSurveyType';

type OrgGoalContextType = {
  goal: OrgGoal | null;
  recentStats: Stats | null;
  questions: string[];
  reloadGoal: () => Promise<void>;
  setGoal: (goal: OrgGoal | null) => void;
}

const OrgGoalContext = createContext<OrgGoalContextType>({ goal: null, recentStats: null, questions: [], reloadGoal: async () => {}, setGoal: () => {} });

type OrgGoalProviderProps = {
  children: ReactNode;
  goalId?: string;
};

export const useOrgGoal = () => {
  return useContext(OrgGoalContext);
};

export const OrgGoalProvider: React.FC<OrgGoalProviderProps> = ({ children, goalId }) => {
  const { get } = useApiData();
  const [goal, setGoal] = useState<OrgGoal | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);
  const [recentStats, setRecentStats] = useState<Stats | null>(null);

  const mapToScoreObject = (scoreString: string, nextQuestions: string[]) => {
    const scores = scoreString.split(',');

    const mappedArr = scores.map((s, i) => {
      try {
        const value = parseFloat(s);
        return { question: nextQuestions[i], score: value };
      } catch {
        return { question: nextQuestions[i], score: 0 };
      }
    });
    return scoreResult2ScoresObject(mappedArr);
  }

  const reloadGoal = useCallback(async () => {
    if (!goalId) {
      return;
    }
    const result = await get(`/organization-goal/${goalId}`);
    if (result) {
      const nextQuestions = getQuestionsForSurveyType(result.surveyType);
      setQuestions(nextQuestions);
      setGoal({ ...result, scoreData: mapToScoreObject(result.originalScores, nextQuestions) });
    }
  }, [get, goalId]);

  useEffect(() => {
    const getRecentScore = async () => {
      const response = await get(`/organization/stats?fromDate=${goal?.registered?.substring(0, 10)}&toDate=${goal?.endDate}&surveyType=${goal?.surveyType}`);
      setRecentStats(response);
    }
    if (goal) {
      getRecentScore();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal])

  useEffect(() => {
    if (!goalId) {
      return;
    }
    reloadGoal();
  }, [reloadGoal]);

  return (
    <OrgGoalContext.Provider value={{ goal, recentStats, questions, reloadGoal, setGoal }}>
      {children}
    </OrgGoalContext.Provider>
  );
};
